import {Component, HostBinding, HostListener, NgZone, OnDestroy, signal, WritableSignal, ChangeDetectionStrategy} from '@angular/core';
import {Subscription} from 'rxjs';
import {ToastService} from './bs-toast.service';
import {ToastPackage} from './config';
import {IConfigIndividual} from './entities/config.individual';
import {IToastIconClasses} from './entities/toast-icon-classes';

@Component({
  selector: 'toast-component',
  imports: [],
  template: `
    @if (title && options.closeButton) {
      <div class="toast-header p-2">
        <!-- <img src="..." class="rounded me-2" alt="...">-->
        <!-- @switch (toastType) {
            @case ('success') {
              <svg class="me-2" width="20" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="rgba(19.24, 91.52, 1.56, 0.999999)" d="M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z"/></svg>
            }
            @case ('info') {
              <svg class="me-2" width="20" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="rgba(0, 78.52, 122.2, 0.999999)" d="M256 8C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm0 110c23.196 0 42 18.804 42 42s-18.804 42-42 42-42-18.804-42-42 18.804-42 42-42zm56 254c0 6.627-5.373 12-12 12h-88c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h12v-64h-12c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h64c6.627 0 12 5.373 12 12v100h12c6.627 0 12 5.373 12 12v24z"/></svg>
            }
            @case ('error') {
              <svg class="me-2" width="20" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="rgba(130, 30.68, 15.08, 0.999999)" d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm121.6 313.1c4.7 4.7 4.7 12.3 0 17L338 377.6c-4.7 4.7-12.3 4.7-17 0L256 312l-65.1 65.6c-4.7 4.7-12.3 4.7-17 0L134.4 338c-4.7-4.7-4.7-12.3 0-17l65.6-65-65.6-65.1c-4.7-4.7-4.7-12.3 0-17l39.6-39.6c4.7-4.7 12.3-4.7 17 0l65 65.7 65.1-65.6c4.7-4.7 12.3-4.7 17 0l39.6 39.6c4.7 4.7 4.7 12.3 0 17L312 256l65.6 65.1z"/></svg>
            }
            @default {
              <svg aria-hidden="true" class="me-2" height="20" width="20" xmlns="http://www.w3.org/2000/svg"><rect width="100%" height="100%" fill="#007aff" /></svg>
            }
          }-->
        <svg aria-hidden="true" class="rounded me-2 text-{{ toastType }}" height="20" width="20" xmlns="http://www.w3.org/2000/svg">
          <rect width="100%" height="100%" fill="currentColor" />
        </svg>
        <div [class]=""></div>
        <strong class="me-auto">{{ title }}</strong>
        @if (duplicatesCount) {
          <small class="text-body-secondary">{{ duplicatesCount + 1 }}</small>
        }
        <button (click)="remove()" type="button" class="btn-close" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
      @if (options.enableHtml) {
        <div class="toast-body p-2" [innerHTML]="message"></div>
      } @else {
        <div class="toast-body p-2">{{ message }}</div>
      }
    } @else {
      <div class="d-flex p-2">
        @if (options.enableHtml) {
          <div class="toast-body w-100" [innerHTML]="message"></div>
        } @else {
          <div class="toast-body">{{ message }}</div>
        }
        @if (options.closeButton) {
          <button
            (click)="remove()"
            type="button"
            class="btn-close btn-close-white"
            [class.m-auto]="!!title"
            [class.ms-auto]="!title"
            data-bs-dismiss="toast"
            aria-label="Close"
          ></button>
        }
      </div>
    }
  `,
  styles: `
    :host {
      opacity: 0;
      transition: opacity var(--easeTime, 200ms) var(--easing, ease-in-out);
    }
    :host.toast-active {
      opacity: 1;
    }
    :host.toast-inactive {
      opacity: 0;
    }
    :host.toast-removed {
      opacity: 0;
    }
  `,
})
export class ToastComponent<ConfigPayload = any> implements OnDestroy {
  message?: string | null;
  title?: string;
  options: IConfigIndividual<ConfigPayload>;
  duplicatesCount!: number;
  originalTimeout: number;

  /** width of progress bar */
  width = signal(-1);

  /** a combination of toast type and options.toastClass */
  @HostBinding('class') toastClasses = '';
  toastType: keyof IToastIconClasses | string;

  state: WritableSignal<{
    value: 'inactive' | 'active' | 'removed';
    params: {easeTime: number | string; easing: string};
  }>;

  @HostBinding('class.toast-inactive') get isInactive() {
    return this.state().value === 'inactive';
  }

  @HostBinding('class.toast-active') get isActive() {
    return this.state().value === 'active';
  }

  @HostBinding('class.toast-removed') get isRemoved() {
    return this.state().value === 'removed';
  }

  // щоб зберегти твої параметри easeTime/easing з state().params :contentReference[oaicite:6]{index=6}
  @HostBinding('style.--easeTime') get easeTimeCss() {
    return `${this.state().params.easeTime}ms`;
  }
  @HostBinding('style.--easing') get easingCss() {
    return this.state().params.easing;
  }

  private timeout: any;
  private intervalId: any;
  private hideTime!: number;
  private sub: Subscription;
  private sub1: Subscription;
  private sub2: Subscription;
  private sub3: Subscription;

  @HostBinding('style.--easeTime')
  get _t() {
    return `${this.state().params.easeTime}ms`;
  }
  @HostBinding('style.--easing')
  get _e() {
    return `${this.state().params.easeTime}ms`;
  }

  constructor(
    protected toastService: ToastService,
    public toastPackage: ToastPackage,
    protected ngZone?: NgZone
  ) {
    this.message = toastPackage.message;
    this.title = toastPackage.title;

    this.options = toastPackage.config;
    this.originalTimeout = toastPackage.config.timeOut;
    // fade show
    const typeClasses = toastPackage.config.iconClasses[toastPackage.toastType] || '';

    this.toastType = toastPackage.toastType === 'error' ? 'danger' : toastPackage.toastType;

    // toast-${toastPackage.toastType}
    this.toastClasses = `${typeClasses} ${toastPackage.config.toastClass}`;

    this.sub = toastPackage.toastRef.afterActivate().subscribe(() => {
      this.activateToast();
    });
    this.sub1 = toastPackage.toastRef.manualClosed().subscribe(() => {
      this.remove();
    });
    this.sub2 = toastPackage.toastRef.timeoutReset().subscribe(() => {
      this.resetTimeout();
    });
    this.sub3 = toastPackage.toastRef.countDuplicate().subscribe((count) => {
      this.duplicatesCount = count;
    });
    this.state = signal({
      value: 'inactive',
      params: {
        easeTime: this.toastPackage.config.easeTime,
        easing: 'ease-in',
      },
    });
  }
  ngOnDestroy() {
    this.sub.unsubscribe();
    this.sub1.unsubscribe();
    this.sub2.unsubscribe();
    this.sub3.unsubscribe();
    clearInterval(this.intervalId);
    clearTimeout(this.timeout);
  }
  /**
   * activates toast and sets timeout
   */
  activateToast() {
    this.state.update((state) => ({...state, value: 'active'}));
    if (!(this.options.disableTimeOut === true || this.options.disableTimeOut === 'timeOut') && this.options.timeOut) {
      this.outsideTimeout(() => this.remove(), this.options.timeOut);
      this.hideTime = new Date().getTime() + this.options.timeOut;
      if (this.options.progressBar) {
        this.outsideInterval(() => this.updateProgress(), 10);
      }
    }
  }
  /**
   * updates progress bar width
   */
  updateProgress() {
    if (this.width() === 0 || this.width() === 100 || !this.options.timeOut) {
      return;
    }
    const now = new Date().getTime();
    const remaining = this.hideTime - now;
    this.width.set((remaining / this.options.timeOut) * 100);
    if (this.options.progressAnimation === 'increasing') {
      this.width.update((width) => 100 - width);
    }
    if (this.width() <= 0) {
      this.width.set(0);
    }
    if (this.width() >= 100) {
      this.width.set(100);
    }
  }

  resetTimeout() {
    clearTimeout(this.timeout);
    clearInterval(this.intervalId);
    this.state.update((state) => ({...state, value: 'active'}));

    this.outsideTimeout(() => this.remove(), this.originalTimeout);
    this.options.timeOut = this.originalTimeout;
    this.hideTime = new Date().getTime() + (this.options.timeOut || 0);
    this.width.set(-1);
    if (this.options.progressBar) {
      this.outsideInterval(() => this.updateProgress(), 10);
    }
  }

  /**
   * tells toastService to remove this toast after animation time
   */
  remove() {
    if (this.state().value === 'removed') {
      return;
    }
    clearTimeout(this.timeout);
    this.state.update((state) => ({...state, value: 'removed'}));
    this.outsideTimeout(() => this.toastService.remove(this.toastPackage.toastId), +this.toastPackage.config.easeTime);
  }
  @HostListener('click')
  tapToast() {
    if (this.state().value === 'removed') {
      return;
    }
    this.toastPackage.triggerTap();
    if (this.options.tapToDismiss) {
      this.remove();
    }
  }
  @HostListener('mouseenter')
  stickAround() {
    if (this.state().value === 'removed') {
      return;
    }

    if (this.options.disableTimeOut !== 'extendedTimeOut') {
      clearTimeout(this.timeout);
      this.options.timeOut = 0;
      this.hideTime = 0;

      // disable progressBar
      clearInterval(this.intervalId);
      this.width.set(0);
    }
  }
  @HostListener('mouseleave')
  delayedHideToast() {
    if (
      this.options.disableTimeOut === true ||
      this.options.disableTimeOut === 'extendedTimeOut' ||
      this.options.extendedTimeOut === 0 ||
      this.state().value === 'removed'
    ) {
      return;
    }
    this.outsideTimeout(() => this.remove(), this.options.extendedTimeOut);
    this.options.timeOut = this.options.extendedTimeOut;
    this.hideTime = new Date().getTime() + (this.options.timeOut || 0);
    this.width.set(-1);
    if (this.options.progressBar) {
      this.outsideInterval(() => this.updateProgress(), 10);
    }
  }

  outsideTimeout(func: () => any, timeout: number) {
    if (this.ngZone) {
      this.ngZone.runOutsideAngular(() => (this.timeout = setTimeout(() => this.runInsideAngular(func), timeout)));
    } else {
      this.timeout = setTimeout(() => func(), timeout);
    }
  }

  outsideInterval(func: () => any, timeout: number) {
    if (this.ngZone) {
      this.ngZone.runOutsideAngular(() => (this.intervalId = setInterval(() => this.runInsideAngular(func), timeout)));
    } else {
      this.intervalId = setInterval(() => func(), timeout);
    }
  }

  private runInsideAngular(func: () => any) {
    if (this.ngZone) {
      this.ngZone.run(() => func());
    } else {
      func();
    }
  }
}
